/**
 * Copyright © 2024 Delicious AI, LLC
 * @author Stockton Jenkins <stockton.jenkins@deliciousai.com>
 */

import { NavigateOptions, useNavigate } from 'react-router-dom'
import { pickBy, isString, isNumber, omit } from 'lodash'
import { useEffect } from 'react'
import { StringHelpers } from '../helpers/string.helpers'

export const useUrlSearchParams = <
  T extends Record<string, string | string[] | number>,
>(
  initialState: Partial<T> = {},
) => {
  const navigate = useNavigate()

  useEffect(() => {
    if (initialState && StringHelpers.isNullOrEmpty(window.location.search)) {
      handleUpdateSearchParams(initialState)
    }
  }, [])

  const parseUrlSearchParams = (searchString: string): Partial<T> => {
    const search = new URLSearchParams(searchString)
    const params = Object.fromEntries(
      Array.from(search.entries()).map(([key, value]) =>
        key.endsWith('[]')
          ? [key.replace('[]', ''), String(value).split(',')]
          : !isNaN(Number(value))
            ? [key, value]
            : [key, value],
      ),
    )
    return params as unknown as Partial<T>
  }

  const isValidValue = (value: any) =>
    isString(value) ||
    isNumber(value) ||
    (Array.isArray(value) && !!value.length)

  const handleSetSearchParams = (params: T) => {
    const entries: [string, string | string[] | number][] = Object.entries(
      pickBy(params, isValidValue),
    )
    const path = `${window.location.pathname}?${entries
      .map(([key, value]) =>
        Array.isArray(value)
          ? `${key}[]=${value.join(',')}`
          : `${key}=${value}`,
      )
      .join('&')}`
    navigate(path)
  }

  const handleUpdateSearchParams = (
    params: Partial<T> = {},
    options?: NavigateOptions & {
      replaceUrl?: boolean
    },
  ) => {
    const currentState = parseUrlSearchParams(window.location.search)
    const entries: [string, string | string[]][] = Object.entries(
      pickBy(
        {
          ...currentState,
          ...params,
        },
        isValidValue,
      ),
    )
    if (!entries.length) {
      navigate(window.location.pathname, {
        replace: options?.replaceUrl,
        ...options,
      })
    } else {
      const search = entries
        .map(([key, value]) =>
          Array.isArray(value)
            ? `${key}[]=${value.join(',')}`
            : `${key}=${value}`,
        )
        .join('&')
      const path = `${window.location.pathname}?${search}`
      const currentSearch = window.location.search.replace(/^\?/, '')

      if (search !== currentSearch) {
        navigate(path, { replace: options?.replaceUrl, ...options })
      }
    }
  }

  const handleNavTo = (
    path: string | ((current: string) => string),
    options?: {
      resetParams?: boolean
      removeParams?: string[]
      replaceParams?: Partial<T>
      replaceUrl?: boolean
    },
  ) => {
    const _path = (
      typeof path === 'string'
        ? path.startsWith('/')
          ? path
          : `/${path}`
        : path(window.location.pathname)
    ).replace('//', '/')
    const currentState = omit(
      options?.resetParams ? {} : parseUrlSearchParams(window.location.search),
      options?.removeParams || [],
    )

    const entries: [string, string][] = Object.entries(
      pickBy(
        {
          ...currentState,
          ...(options?.replaceParams || {}),
        },
        isValidValue,
      ),
    )
    if (!entries.length) {
      navigate(_path, { replace: options?.replaceUrl })
    } else {
      const newPath = `${_path}?${entries
        .map(([key, value]) =>
          Array.isArray(value)
            ? `${key}[]=${value.join(',')}`
            : `${key}=${value}`,
        )
        .join('&')}`
      navigate(newPath, { replace: options?.replaceUrl })
    }
  }

  return {
    state: parseUrlSearchParams(window.location.search),
    handle: {
      handleSetSearchParams,
      handleUpdateSearchParams,
      handleNavTo,
      handleGoBack: () => navigate(-1),
    },
  }
}
